import { RouteObject } from 'react-router';

import {
  ApiEventId,
  ApiInternshipId,
  ApiJobId,
  ApiOrgId,
  ApiVolopId,
} from 'api/types/ApiTypedId';
import { LayoutHandleProps } from 'layouts/Layout/LayoutHandleProps';
import { orgDashboardApiFetchEvent } from 'modules/orgDashboard/api/listing/orgDashboardApiFetchEvent';
import { orgDashboardApiFetchInternship } from 'modules/orgDashboard/api/listing/orgDashboardApiFetchInternship';
import { orgDashboardApiFetchJob } from 'modules/orgDashboard/api/listing/orgDashboardApiFetchJob';
import { orgDashboardApiFetchVolop } from 'modules/orgDashboard/api/listing/orgDashboardApiFetchVolop';
import { OrgDashboardPreviewEventPageLoadable } from 'modules/orgDashboard/pages/events/preview/OrgDashboardPreviewEventPageLoadable';
import { OrgDashboardViewEventPageLoadable } from 'modules/orgDashboard/pages/events/view/OrgDashboardViewEventPageLoadable';
import { OrgDashboardPreviewInternshipPageLoadable } from 'modules/orgDashboard/pages/internships/preview/OrgDashboardPreviewInternshipPageLoadable';
import { OrgDashboardViewInternshipPageContainerLoadable } from 'modules/orgDashboard/pages/internships/view/OrgDashboardViewInternshipPageContainerLoadable';
import { OrgDashboardPreviewJobPageLoadable } from 'modules/orgDashboard/pages/jobs/preview/OrgDashboardPreviewJobPageLoadable';
import { OrgDashboardViewJobPageContainerLoadable } from 'modules/orgDashboard/pages/jobs/view/OrgDashboardViewJobPageContainerLoadable';
import { OrgDashboardViewListingApplicationPageContainerLoadable } from 'modules/orgDashboard/pages/listings/viewApplication/OrgDashboardViewListingApplicationPageContainerLoadable';
import { OrgDashboardPreviewOrgPageContainerLoadable } from 'modules/orgDashboard/pages/orgs/preview/OrgDashboardPreviewOrgPageContainerLoadable';
import { OrgDashboardRecommendedCandidateDetailPageLoadable } from 'modules/orgDashboard/pages/recommendedCandidates/view/OrgDashboardRecommendedCandidateDetailPageLoadable';
import { OrgDashboardPreviewVolopPageLoadable } from 'modules/orgDashboard/pages/volops/preview/OrgDashboardPreviewVolopPageLoadable';
import { OrgDashboardViewVolopPageLoadable } from 'modules/orgDashboard/pages/volops/view/OrgDashboardViewVolopPageLoadable';
import { NotFoundPageLoadable } from 'pages/NotFound/NotFoundPageLoadable';
import { AppRouteLoaderArgs } from 'routing/classes/types/AppRouteLoaderArgs';
import { loadableLoader } from 'routing/utils/loadableLoader';
import { renderLoader } from 'routing/utils/renderLoader/renderLoader';

import {
  orgDashboardPreviewEventRoute,
  orgDashboardPreviewInternshipRoute,
  orgDashboardPreviewJobRoute,
  orgDashboardPreviewOrgRoute,
  orgDashboardPreviewVolopRoute,
  orgDashboardViewApplicationRoute,
  orgDashboardViewEventRoute,
  orgDashboardViewInternshipRoute,
  orgDashboardViewJobRecommendedCandidatesRoute,
  orgDashboardViewJobRoute,
  orgDashboardViewVolopRoute,
} from './orgDashboard.routes';

export const noSubLayoutOrgDashboardRouteObjects = [
  {
    path: orgDashboardPreviewOrgRoute.relativePath,
    loader: loadableLoader(OrgDashboardPreviewOrgPageContainerLoadable),
    Component: OrgDashboardPreviewOrgPageContainerLoadable,
  },
  {
    path: orgDashboardViewJobRoute.relativePath,
    shouldRevalidate: ({ currentParams, nextParams }) =>
      currentParams.jobId !== nextParams.jobId,
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardViewJobRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const jobId = params.jobId as ApiJobId;

        try {
          const [job] = await Promise.all([
            orgDashboardApiFetchJob(orgId, jobId),
            OrgDashboardViewJobPageContainerLoadable.load(),
          ]);

          return job;
        } catch {
          await NotFoundPageLoadable.load();
          return null;
        }
      },
      render: (job) =>
        job ? (
          <OrgDashboardViewJobPageContainerLoadable key={job.id} job={job} />
        ) : (
          <NotFoundPageLoadable noLayout />
        ),
    }),
  },
  {
    path: orgDashboardViewInternshipRoute.relativePath,
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardViewInternshipRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const internshipId = params.internshipId as ApiInternshipId;

        try {
          const [internship] = await Promise.all([
            orgDashboardApiFetchInternship(orgId, internshipId),
            OrgDashboardViewInternshipPageContainerLoadable.load(),
          ]);

          return internship;
        } catch {
          await NotFoundPageLoadable.load();
          return null;
        }
      },
      render: (internship) =>
        internship ? (
          <OrgDashboardViewInternshipPageContainerLoadable
            key={internship.id}
            internship={internship}
          />
        ) : (
          <NotFoundPageLoadable noLayout />
        ),
    }),
  },
  {
    path: orgDashboardViewVolopRoute.relativePath,
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardViewVolopRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const volopId = params.volopId as ApiVolopId;

        try {
          const [volop] = await Promise.all([
            orgDashboardApiFetchVolop(orgId, volopId),
            OrgDashboardViewVolopPageLoadable.load(),
          ]);

          return volop;
        } catch {
          await NotFoundPageLoadable.load();
          return null;
        }
      },
      render: (volop) =>
        volop ? (
          <OrgDashboardViewVolopPageLoadable key={volop.id} volop={volop} />
        ) : (
          <NotFoundPageLoadable noLayout />
        ),
    }),
  },
  {
    path: orgDashboardViewEventRoute.relativePath,
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardViewEventRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const eventId = params.eventId as ApiEventId;

        try {
          const [event] = await Promise.all([
            orgDashboardApiFetchEvent(orgId, eventId),
            OrgDashboardViewEventPageLoadable.load(),
          ]);

          return event;
        } catch {
          await NotFoundPageLoadable.load();
          return null;
        }
      },
      render: (event) =>
        event ? (
          <OrgDashboardViewEventPageLoadable key={event.id} event={event} />
        ) : (
          <NotFoundPageLoadable noLayout />
        ),
    }),
  },
  {
    path: orgDashboardViewApplicationRoute.relativePath,
    loader: loadableLoader(
      OrgDashboardViewListingApplicationPageContainerLoadable,
    ),
    Component: OrgDashboardViewListingApplicationPageContainerLoadable,
  },
  {
    path: orgDashboardViewJobRecommendedCandidatesRoute.relativePath,
    handle: {
      fallbackUrlByLocaleToCurrent: false,
    } satisfies LayoutHandleProps,
    loader: loadableLoader(OrgDashboardRecommendedCandidateDetailPageLoadable),
    Component: OrgDashboardRecommendedCandidateDetailPageLoadable,
  },
  // Preview listing routes
  {
    path: orgDashboardPreviewJobRoute.relativePath,
    handle: {
      darkBackground: false,
    },
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardPreviewJobRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const jobId = params.jobId as ApiJobId;

        const [job] = await Promise.all([
          orgDashboardApiFetchJob(orgId, jobId),
          OrgDashboardPreviewJobPageLoadable.load(),
        ]);

        return job;
      },
      render: (job) => (
        <OrgDashboardPreviewJobPageLoadable key={job.id} job={job} />
      ),
    }),
  },
  {
    path: orgDashboardPreviewInternshipRoute.relativePath,
    handle: {
      darkBackground: false,
    },
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardPreviewInternshipRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const internshipId = params.internshipId as ApiInternshipId;

        const [internship] = await Promise.all([
          orgDashboardApiFetchInternship(orgId, internshipId),
          OrgDashboardPreviewInternshipPageLoadable.load(),
        ]);

        return internship;
      },
      render: (internship) => (
        <OrgDashboardPreviewInternshipPageLoadable
          key={internship.id}
          internship={internship}
        />
      ),
    }),
  },
  {
    path: orgDashboardPreviewVolopRoute.relativePath,
    handle: {
      darkBackground: false,
    },
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardPreviewVolopRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const volopId = params.volopId as ApiVolopId;

        const [volop] = await Promise.all([
          orgDashboardApiFetchVolop(orgId, volopId),
          OrgDashboardPreviewVolopPageLoadable.load(),
        ]);

        return volop;
      },
      render: (volop) => (
        <OrgDashboardPreviewVolopPageLoadable key={volop.id} volop={volop} />
      ),
    }),
  },
  {
    path: orgDashboardPreviewEventRoute.relativePath,
    handle: {
      darkBackground: false,
    },
    ...renderLoader({
      clientOnly: true,
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardPreviewEventRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const eventId = params.eventId as ApiEventId;

        const [event] = await Promise.all([
          orgDashboardApiFetchEvent(orgId, eventId),
          OrgDashboardPreviewEventPageLoadable.load(),
        ]);

        return event;
      },
      render: (event) => (
        <OrgDashboardPreviewEventPageLoadable key={event.id} event={event} />
      ),
    }),
  },
] satisfies RouteObject[];
